import { client } from '@blazeteams/sdk/client';
import { API_BASE_URL } from '@/api/api-config';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope, sessionTenantId } from '@/features/auth/session-scope';
import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { parseAshleyHint, readAshleySuggestion, type AshleyHint } from '@/features/inbox/suggestion-readback-contract';
import { captureAshleyInteraction, currentAshleyInteraction, onAshleyInteraction } from './ashley-interaction';
import { currentAshleySource } from './ashley-suggestion-freshness';
import { useAiSuggestionStore } from './ai-suggestion-store';
import { chatInstant } from '@/features/team-chat/message-order';
const seen = new Set<string>();
const latest = new Map<string, AshleyHint>();
const pending = new Map<string, AbortController>();
onAshleyInteraction(conversationId => { pending.get(conversationId)?.abort(); pending.delete(conversationId); });
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous)) { for (const controller of pending.values()) controller.abort(); pending.clear(); seen.clear(); latest.clear(); }
});
export async function receiveAshleySuggestionHint(frame: unknown): Promise<void> {
  const hint = parseAshleyHint(frame);
  if (!hint || !currentAshleySource(hint)) return;
  const key = JSON.stringify([hint.conversationId, hint.suggestionId]);
  if (seen.has(key)) return;
  const previous = latest.get(hint.conversationId);
  if (previous && chatInstant(hint.createdAt)! < chatInstant(previous.createdAt)!) return;
  const displayed = useAiSuggestionStore.getState().byConversation[hint.conversationId];
  // A definitively older hint cannot retire a newer displayed proposal. UUID
  // order is never a freshness sequence; equal times are validated by GET.
  if (displayed?.createdAt && chatInstant(hint.createdAt)! < chatInstant(displayed.createdAt)!) return;
  if (displayed?.sourceMessageCreatedAt && chatInstant(hint.sourceMessageCreatedAt)! < chatInstant(displayed.sourceMessageCreatedAt)!) return;
  seen.add(key); latest.set(hint.conversationId, hint);
  // Latest-only: an abstention or failed replacement cannot retain old text.
  if (displayed) useAiSuggestionStore.getState().clearSuggestion(hint.conversationId, displayed);
  const interaction = captureAshleyInteraction(hint.conversationId), tenant = sessionTenantId(useAuthStore.getState());
  const base = client.getConfig?.().baseUrl ?? API_BASE_URL;
  pending.get(hint.conversationId)?.abort();
  const controller = new AbortController(); pending.set(hint.conversationId, controller);
  const current = () => currentAshleyInteraction(interaction) && !controller.signal.aborted && pending.get(hint.conversationId) === controller && base === (client.getConfig?.().baseUrl ?? API_BASE_URL);
  try {
    if (!current()) return;
    const result = await client.get<unknown>({ url: '/inbox/conversations/{id}/suggestions/{suggestionId}', path: { id: hint.conversationId, suggestionId: hint.suggestionId }, signal: controller.signal, cache: 'no-store', headers: { 'Accept-Language': getLocaleSnapshot().locale } });
    if (!current() || result.error || result.response?.status !== 200) return;
    const suggestion = readAshleySuggestion(result.data, tenant, hint);
    if (current() && currentAshleySource(suggestion) && suggestion.suggestion.trim()) useAiSuggestionStore.getState().setSuggestion(suggestion);
  } catch { /* Missing, stale, malformed or failed reads never retry/regenerate. */ }
  finally { if (pending.get(hint.conversationId) === controller) pending.delete(hint.conversationId); }
}
